import React, { useState, useMemo } from 'react';
import { 
  Search, 
  User, 
  MapPin, 
  Calendar,
  Package, 
  Building2, 
  Filter,
  CheckSquare, 
  Tag,
  Layers,
  Hotel,
  Phone,
  Hash,
  Flag,
  Download,
  RefreshCw
} from 'lucide-react';
import { Pilgrim } from '../../types/pilgrim';
import { useLanguage } from '../../contexts/LanguageContext';
import { calculateAge } from '../../utils/dateUtils';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { usePilgrimSelectorStore } from '../../stores/pilgrimSelectorStore';

type Props = {
  pilgrims: Pilgrim[];
};

type SortField = 'name' | 'passportNumber' | 'serialNumber' | 'mobileNumber' | 'country' | 'nationality' | 'dateOfBirth' | 'category' | 'package' | 'group';
type SortDirection = 'asc' | 'desc';

export const PilgrimsTable = ({ pilgrims }: Props) => {
  const { translations, direction } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [packageFilter, setPackageFilter] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [nusukGroupFilter, setNusukGroupFilter] = useState<string>('');
  const [roomTypeFilter, setRoomTypeFilter] = useState<string>('');
  const { updateAllPilgrimSelectors, loading: updatingSelectors } = usePilgrimSelectorStore();
  const [isExporting, setIsExporting] = useState(false);
  
  // Get unique categories
  const categories = useMemo(() => {
    return [...new Set(pilgrims.map(p => p.pilgrim_category))].filter(Boolean).sort();
  }, [pilgrims]);
  
  // Get unique package names
  const packages = useMemo(() => {
    return [...new Set(pilgrims.map(p => p.package_nickName))].filter(Boolean).sort();
  }, [pilgrims]);
  
  // Get unique nusuk groups
  const nusukGroups = useMemo(() => {
    return [...new Set(pilgrims.map(p => p.nusuk_group))].filter(Boolean).sort();
  }, [pilgrims]);
  
  // Filter pilgrims based on search term and filters
  const filteredPilgrims = useMemo(() => {
    return pilgrims.filter(pilgrim => {
      const matchesSearch = !searchTerm || 
        pilgrim.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(pilgrim.Passport_Number)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(pilgrim.Serial_Number)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(pilgrim.Mobile_Number)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pilgrim.Residence_Country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pilgrim.Nationality_Country?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = !categoryFilter || pilgrim.pilgrim_category === categoryFilter;
      const matchesPackage = !packageFilter || pilgrim.package_nickName === packageFilter;
      const matchesGroup = !nusukGroupFilter || pilgrim.nusuk_group === nusukGroupFilter;
      const matchesRoomType = !roomTypeFilter || 
        pilgrim.main_makkah_roomType === roomTypeFilter ||
        pilgrim.shifting_makkah_roomType === roomTypeFilter ||
        pilgrim.madinah_roomType === roomTypeFilter;
      
      return matchesSearch && matchesCategory && matchesPackage && matchesGroup && matchesRoomType;
    });
  }, [pilgrims, searchTerm, categoryFilter, packageFilter, nusukGroupFilter, roomTypeFilter]);
  
  // Sort pilgrims
  const sortedPilgrims = useMemo(() => {
    return [...filteredPilgrims].sort((a, b) => {
      let valueA, valueB;
      
      switch (sortField) {
        case 'name':
          valueA = a.Name || '';
          valueB = b.Name || '';
          break;
        case 'passportNumber':
          valueA = String(a.Passport_Number) || '';
          valueB = String(b.Passport_Number) || '';
          break;
        case 'serialNumber':
          valueA = String(a.Serial_Number) || '';
          valueB = String(b.Serial_Number) || '';
          break;
        case 'mobileNumber':
          valueA = String(a.Mobile_Number) || '';
          valueB = String(b.Mobile_Number) || '';
          break;
        case 'country':
          valueA = a.Residence_Country || '';
          valueB = b.Residence_Country || '';
          break;
        case 'nationality':
          valueA = a.Nationality_Country || '';
          valueB = b.Nationality_Country || '';
          break;
        case 'dateOfBirth':
          valueA = a.Date_of_Birth ? calculateAge(a.Date_of_Birth) : 0;
          valueB = b.Date_of_Birth ? calculateAge(b.Date_of_Birth) : 0;
          break;
        case 'category':
          valueA = a.pilgrim_category || '';
          valueB = b.pilgrim_category || '';
          break;
        case 'package':
          valueA = a.package_nickName || '';
          valueB = b.package_nickName || '';
          break;
        case 'group':
          valueA = a.nusuk_group || '';
          valueB = b.nusuk_group || '';
          break;
        default:
          valueA = a.Name || '';
          valueB = b.Name || '';
      }
      
      const comparisonValue = typeof valueA === 'string' ? 
        valueA.localeCompare(valueB) : 
        valueA - valueB;
        
      return sortDirection === 'asc' ? comparisonValue : -comparisonValue;
    });
  }, [filteredPilgrims, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(sortedPilgrims.length / itemsPerPage);
  const displayedPilgrims = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedPilgrims.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, itemsPerPage, sortedPilgrims]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleUpdateAllSelectors = async () => {
    try {
      await updateAllPilgrimSelectors();
    } catch (error) {
      console.error('Error updating selectors:', error);
    }
  };

  const handleExportData = async () => {
    try {
      setIsExporting(true);
      
      // Create rows for export
      const exportData = filteredPilgrims.map(pilgrim => ({
        'Serial Number': pilgrim.Serial_Number,
        'Name': pilgrim.Name,
        'Passport Number': pilgrim.Passport_Number,
        'Mobile Number': pilgrim.Mobile_Number,
        'Email': pilgrim.Email,
        'Gender': pilgrim.Gender,
        'Date of Birth': pilgrim.Date_of_Birth,
        'Age': pilgrim.Date_of_Birth ? calculateAge(pilgrim.Date_of_Birth) : '',
        'Country': pilgrim.Residence_Country,
        'Nationality': pilgrim.Nationality_Country,
        'Category': pilgrim.pilgrim_category,
        'Package': pilgrim.package_nickName,
        'Nusuk Group': pilgrim.nusuk_group,
        'Flight Type': pilgrim.Flight_Contract_Type,
        'PNR': pilgrim.PNR_NO,
        'Flight Ticket': pilgrim.flight_Ticket,
        'Pilgrim Type': pilgrim.Pilgrim_Type,
        'Main Makkah Room Type': pilgrim.main_makkah_roomType,
        'Shifting Makkah Room Type': pilgrim.shifting_makkah_roomType,
        'Madinah Room Type': pilgrim.madinah_roomType
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Add column widths
      ws['!cols'] = [
        { wch: 15 }, // Serial Number
        { wch: 30 }, // Name
        { wch: 15 }, // Passport Number
        { wch: 15 }, // Mobile Number
        { wch: 25 }, // Email
        { wch: 10 }, // Gender
        { wch: 12 }, // Date of Birth
        { wch: 8 },  // Age
        { wch: 15 }, // Country
        { wch: 15 }, // Nationality
        { wch: 10 }, // Category
        { wch: 25 }, // Package
        { wch: 15 }, // Nusuk Group
        { wch: 12 }, // Flight Type
        { wch: 12 }, // PNR
        { wch: 15 }, // Flight Ticket
        { wch: 12 }, // Pilgrim Type
        { wch: 15 }, // Main Makkah Room Type
        { wch: 15 }, // Shifting Makkah Room Type
        { wch: 15 }  // Madinah Room Type
      ];
      
      // Create workbook and add sheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pilgrims');
      
      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      const filename = `pilgrims-export-${date}.xlsx`;
      
      // Save file
      XLSX.writeFile(wb, filename);
      toast.success('Pilgrims data exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Filter Controls */}
      <div className="p-4 border-b">
        <div className="flex flex-col md:flex-row gap-4 mb-4 justify-between">
          {/* Left side: Search */}
          <div className="relative w-full md:w-1/3">
            <input
              type="text"
              placeholder={translations?.pilgrimsTable?.searchPlaceholder || "Search pilgrims..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          </div>
          
          {/* Right side: Action buttons */}
          <div className="flex gap-2">
            {/* Export Data Button */}
            <button
              onClick={handleExportData}
              disabled={isExporting || filteredPilgrims.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
            >
              <Download size={16} className={isExporting ? 'animate-pulse' : ''} />
              <span>{isExporting ? 'Exporting...' : 'Export Data'}</span>
            </button>
            
            {/* Update Selectors Button - Admin Only */}
            {localStorage.getItem('userRole') === 'admin' && (
              <button
                onClick={handleUpdateAllSelectors}
                disabled={updatingSelectors}
                className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <RefreshCw size={16} className={updatingSelectors ? 'animate-spin' : ''} />
                <span>{updatingSelectors ? 'Updating...' : 'Update Selector Data'}</span>
              </button>
            )}
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          
          {/* Package Filter */}
          <select
            value={packageFilter}
            onChange={(e) => setPackageFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Packages</option>
            {packages.map(pkg => (
              <option key={pkg} value={pkg}>{pkg}</option>
            ))}
          </select>
          
          {/* Nusuk Group Filter */}
          <select
            value={nusukGroupFilter}
            onChange={(e) => setNusukGroupFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Nusuk Groups</option>
            {nusukGroups.map(group => (
              <option key={group} value={group}>{group}</option>
            ))}
          </select>
          
          {/* Room Type Filter */}
          <select
            value={roomTypeFilter}
            onChange={(e) => setRoomTypeFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Room Types</option>
            <option value="double">Double</option>
            <option value="triple">Triple</option>
            <option value="quadruple">Quadruple</option>
          </select>
        </div>
        
        {/* Active Filters */}
        {(searchTerm || categoryFilter || packageFilter || nusukGroupFilter || roomTypeFilter) && (
          <div className="flex items-center gap-2 mt-3">
            <Filter size={16} className="text-primary-500" />
            <span className="text-sm text-gray-600">Active Filters:</span>
            <div className="flex flex-wrap gap-2">
              {searchTerm && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-md">
                  Search: {searchTerm}
                </span>
              )}
              {categoryFilter && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-md flex items-center">
                  <CheckSquare size={12} className="mr-1" /> {categoryFilter}
                </span>
              )}
              {packageFilter && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-md flex items-center">
                  <Package size={12} className="mr-1" /> {packageFilter}
                </span>
              )}
              {nusukGroupFilter && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-md flex items-center">
                  <Tag size={12} className="mr-1" /> {nusukGroupFilter}
                </span>
              )}
              {roomTypeFilter && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-md flex items-center">
                  <Hotel size={12} className="mr-1" /> {roomTypeFilter}
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* Stats Summary */}
        <div className="mt-4 text-sm text-gray-500 flex gap-4">
          <div>Total: {pilgrims.length} pilgrims</div>
          <div>Filtered: {filteredPilgrims.length} pilgrims</div>
          <div>Showing: {displayedPilgrims.length} pilgrims</div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-2">
                  <User size={14} />
                  <span>{translations?.pilgrimsTable?.headers?.name || 'Name'}</span>
                  {renderSortIcon('name')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('serialNumber')}
              >
                <div className="flex items-center gap-2">
                  <Hash size={14} />
                  <span>{translations?.pilgrimsTable?.headers?.serialNumber || 'Serial No.'}</span>
                  {renderSortIcon('serialNumber')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('passportNumber')}
              >
                <div className="flex items-center gap-2">
                  <span>{translations?.pilgrimsTable?.headers?.passport || 'Passport'}</span>
                  {renderSortIcon('passportNumber')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('mobileNumber')}
              >
                <div className="flex items-center gap-2">
                  <Phone size={14} />
                  <span>{translations?.pilgrimsTable?.headers?.mobile || 'Mobile'}</span>
                  {renderSortIcon('mobileNumber')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('country')}
              >
                <div className="flex items-center gap-2">
                  <MapPin size={14} />
                  <span>{translations?.pilgrimsTable?.headers?.country || 'Country'}</span>
                  {renderSortIcon('country')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('nationality')}
              >
                <div className="flex items-center gap-2">
                  <Flag size={14} />
                  <span>{translations?.pilgrimsTable?.headers?.nationality || 'Nationality'}</span>
                  {renderSortIcon('nationality')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('package')}
              >
                <div className="flex items-center gap-2">
                  <Package size={14} />
                  <span>{translations?.pilgrimsTable?.headers?.package || 'Package'}</span>
                  {renderSortIcon('package')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('dateOfBirth')}
              >
                <div className="flex items-center gap-2">
                  <Calendar size={14} />
                  <span>{translations?.pilgrimsTable?.headers?.age || 'Age'}</span>
                  {renderSortIcon('dateOfBirth')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('category')}
              >
                <div className="flex items-center gap-2">
                  <CheckSquare size={14} />
                  <span>{translations?.pilgrimsTable?.headers?.category || 'Category'}</span>
                  {renderSortIcon('category')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('group')}
              >
                <div className="flex items-center gap-2">
                  <Layers size={14} />
                  <span>{translations?.pilgrimsTable?.headers?.group || 'Nusuk Group'}</span>
                  {renderSortIcon('group')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                <div className="flex items-center gap-2">
                  <Hotel size={14} />
                  <span>{translations?.pilgrimsTable?.headers?.roomTypes || 'Room Types'}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {displayedPilgrims.map((pilgrim, index) => (
              <tr key={pilgrim.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className={`rounded-full w-8 h-8 flex items-center justify-center 
                      ${pilgrim.Gender === 'male' ? 'bg-blue-100 text-blue-500' : 'bg-pink-100 text-pink-500'}`
                    }>
                      <User size={16} />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{pilgrim.Name}</div>
                      <div className="text-xs text-gray-500">
                        {pilgrim.Email || 'No email provided'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{pilgrim.Serial_Number || 'N/A'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{pilgrim.Passport_Number}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{pilgrim.Mobile_Number || 'N/A'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-gray-500" />
                    <div className="text-sm text-gray-900">{pilgrim.Residence_Country}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Flag size={14} className="text-gray-500" />
                    <div className="text-sm text-gray-900">{pilgrim.Nationality_Country || 'N/A'}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{pilgrim.package_nickName}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {pilgrim.Date_of_Birth ? (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        calculateAge(pilgrim.Date_of_Birth) > 60 
                          ? 'bg-red-100 text-red-800' 
                          : calculateAge(pilgrim.Date_of_Birth) > 40
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {calculateAge(pilgrim.Date_of_Birth)}
                      </span>
                    ) : (
                      'N/A'
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    pilgrim.pilgrim_category === 'B2C' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {pilgrim.pilgrim_category}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs rounded-full bg-primary-100 text-primary-800">
                    {pilgrim.nusuk_group || 'N/A'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col gap-1 text-xs">
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Makkah: </span>
                      <span className="px-2 py-0.5 rounded bg-gray-100">
                        {pilgrim.main_makkah_roomType || 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Shifting: </span>
                      <span className="px-2 py-0.5 rounded bg-gray-100">
                        {pilgrim.shifting_makkah_roomType || 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Madinah: </span>
                      <span className="px-2 py-0.5 rounded bg-gray-100">
                        {pilgrim.madinah_roomType || 'N/A'}
                      </span>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination Controls */}
      <div className="px-6 py-4 border-t">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-700">
              {translations?.pagination?.showing || 'Showing'} 
              {' '}{(currentPage - 1) * itemsPerPage + 1}-
              {Math.min(currentPage * itemsPerPage, filteredPilgrims.length)}{' '}
              {translations?.pagination?.of || 'of'}{' '}
              {filteredPilgrims.length} {translations?.pagination?.pilgrims || 'pilgrims'}
            </span>
          
            <div>
              <select
                value={itemsPerPage}
                onChange={e => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-2 py-1 border rounded-md"
              >
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
              </select>
            </div>
          </div>
          
          <div className="inline-flex gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
            >
              {translations?.pagination?.previous || 'Previous'}
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
            >
              {translations?.pagination?.next || 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};